import { useEffect, useState } from "react";
import { Row, Col } from "react-bootstrap";
import { ColumnDef } from "@tanstack/react-table";
import {
  faTrashCan,
  faEnvelopeOpen,
  faEnvelope,
} from "@fortawesome/pro-solid-svg-icons";
import {
  useV2ListNotificationsQuery,
  useV2NotificationsMarkAllReadMutation,
  useV2NotificationsUnreadCountQuery,
  useV2UpdateNotificationMutation,
} from "@generated";
import useMarkNotificationRead, {
  NOTIFICATIONS_CHANGED_EVENT,
} from "@common/hooks/useMarkNotificationRead";
import NotificationRow from "@components/NotificationRow";
import DataTable from "@components/DataTable/DataTable";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import ConfirmationModal from "@components/Modals/ConfirmationModal";
import { BatchActionPill } from "@components/CatalogIndex";
import "./styles.css";

interface NotificationRowData {
  id: string;
  body: string | undefined;
  link: string | undefined;
  readAt: string | undefined | null;
  createdAt: string | undefined;
}

const NotificationsMspa = (): JSX.Element => {
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showDismissModal, setShowDismissModal] = useState(false);
  const handleView = useMarkNotificationRead();

  const { data, isLoading, refetch } = useV2ListNotificationsQuery({
    page: { number: pageNumber, size: pageSize },
  });
  const { refetch: refetchUnreadCount } = useV2NotificationsUnreadCountQuery();
  const [markAllRead] = useV2NotificationsMarkAllReadMutation();
  const [updateNotification] = useV2UpdateNotificationMutation();

  useEffect(() => {
    window.addEventListener(NOTIFICATIONS_CHANGED_EVENT, refetch);
    return () =>
      window.removeEventListener(NOTIFICATIONS_CHANGED_EVENT, refetch);
  }, [refetch]);

  const handleMarkAllRead = async () => {
    await markAllRead().unwrap();
    refetch();
    refetchUnreadCount();
  };

  const handleDismissSelected = async () => {
    await Promise.all(
      selectedIds.map((id) =>
        updateNotification({
          id,
          body: {
            data: {
              id,
              type: "notifications",
              attributes: { "dismissed-at": new Date().toISOString() },
            },
          },
        }).unwrap()
      )
    );
    setSelectedIds([]);
    setShowDismissModal(false);
    refetch();
    refetchUnreadCount();
  };

  const handleMarkSelectedRead = async () => {
    await Promise.all(
      selectedIds.map((id) =>
        updateNotification({
          id,
          body: {
            data: {
              id,
              type: "notifications",
              attributes: { "read-at": new Date().toISOString() },
            },
          },
        }).unwrap()
      )
    );
    setSelectedIds([]);
    refetch();
    refetchUnreadCount();
  };

  const handleMarkSelectedUnread = async () => {
    await Promise.all(
      selectedIds.map((id) =>
        updateNotification({
          id,
          body: {
            data: {
              id,
              type: "notifications",
              attributes: { "read-at": null as unknown as string },
            },
          },
        }).unwrap()
      )
    );
    setSelectedIds([]);
    refetch();
    refetchUnreadCount();
  };

  const notifications: NotificationRowData[] = (data?.data ?? [])
    .filter((notification) => !!notification.id)
    .map((notification) => ({
      id: notification.id as string,
      body: notification.attributes.body,
      link: notification.attributes.link,
      readAt: notification.attributes["read-at"],
      createdAt: notification.attributes["created-at"],
    }));

  const pageMeta = {
    recordCount: data?.meta?.["record-count"] ?? 0,
    pageCount: data?.meta?.["page-count"] ?? 0,
  };

  const columns: ColumnDef<NotificationRowData>[] = [
    {
      id: "notification",
      header: () => (
        <div className="notificationsColumnHeader">
          Notification
          <button
            type="button"
            className="notificationsClearAllButton"
            onClick={handleMarkAllRead}
          >
            Mark All Read
          </button>
        </div>
      ),
      cell: (props) => (
        <NotificationRow
          id={props.row.original.id}
          body={props.row.original.body}
          link={props.row.original.link}
          readAt={props.row.original.readAt}
          createdAt={props.row.original.createdAt}
          onView={handleView}
          variant="table"
        />
      ),
    },
  ];

  return (
    <div className="notificationsWrapper">
      <Row>
        <Col>
          <h1 className="notificationsTitle">Notifications</h1>
        </Col>
        <hr className="notificationsLine" />
      </Row>
      <Row>
        <Col>
          <div className="notificationsBatchActionPill">
            <BatchActionPill
              selectedCount={selectedIds.length}
              alwaysVisible
              actions={[
                {
                  icon: faTrashCan,
                  label: "Archive",
                  onClick: () => setShowDismissModal(true),
                  disabled: selectedIds.length === 0,
                },
                {
                  icon: faEnvelopeOpen,
                  label: "Mark read",
                  onClick: handleMarkSelectedRead,
                  disabled: selectedIds.length === 0,
                },
                {
                  icon: faEnvelope,
                  label: "Mark unread",
                  onClick: handleMarkSelectedUnread,
                  disabled: selectedIds.length === 0,
                },
              ]}
            />
          </div>
          {isLoading ? (
            <div className="notificationsStateCard">
              <ScopeStackSpinner />
            </div>
          ) : notifications.length === 0 ? (
            <div className="notificationsStateCard notificationsEmptyState">
              <h3>No notifications yet</h3>
            </div>
          ) : (
            <DataTable
              data={notifications}
              columns={columns}
              striped
              bordered
              selectable
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              totalPages={pageMeta.pageCount}
              totalRows={pageMeta.recordCount}
              currentPage={pageNumber}
              setCurrentPage={setPageNumber}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              paginationEnabled
              pageSizeEnabled
            />
          )}
        </Col>
      </Row>
      <ConfirmationModal
        show={showDismissModal}
        title="Archive Notifications"
        message={`Are you sure you want to archive ${
          selectedIds.length
        } notification${selectedIds.length === 1 ? "" : "s"}?`}
        onConfirm={handleDismissSelected}
        onCancel={() => setShowDismissModal(false)}
      />
    </div>
  );
};

export default NotificationsMspa;
